/**
 * Модель для работы с отчётом "Тепловая карта"
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';

import { IOffice, IMap, IReportHeatMapPoint, IEmployeeShort, ICoordXY, ICoordXYXY } from './types';
import { IGetHeatMapFilter as IFilter } from '../services/ReportService';
import getInitialFilter from '../utils/getInitialFilter';
import setStartEndOfDay from '../utils/setStartEndOfDay';

import MapsService from '../services/MapsService';
import UsersService from '../services/UsersService';
import ReportService from '../services/ReportService';

interface IControl {
  zoom: number;
  center: ICoordXY;
  bounds: ICoordXYXY;
}
interface ILayers {
  isGeo: boolean;
  isUsers: boolean;
  isAreas: boolean;
  isInfrastructure: boolean;
  isHeatMap: boolean;
  isCameras: boolean;
}
interface IData {
  offices: IOffice[];
  employees: IEmployeeShort[];
}

export interface IHeatMap {
  map: IMap;
  planImg: string;
  data: IReportHeatMapPoint[];
}

interface IState {
  data: IData;
  filter: IFilter;
  control: IControl;
  layers: ILayers;
  heatMap: IHeatMap | null;
}

const initialFilter: IFilter = {
  mapId: '',
  users: [],
  from: new Date().toDateString(),
  to: new Date().toDateString()
};

const initialState: IState = {
  data: {
    offices: [],
    employees: []
  },
  control: {
    zoom: 0,
    center: [0, 0],
    bounds: [
      [0, 0],
      [0, 0]
    ]
  },
  layers: {
    isGeo: true,
    isUsers: true,
    isAreas: true,
    isInfrastructure: false,
    isHeatMap: false,
    isCameras: false
  },
  filter: getInitialFilter(initialFilter),
  heatMap: null
};

export const reportHeatMap = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setData(state: IState, payload: IData): IState {
      return { ...state, data: payload };
    },
    setFocus(state: IState, payload: Partial<IControl>): IState {
      return {
        ...state,
        control: {
          ...state.control,
          ...payload
        }
      };
    },
    setControl(state: IState, payload: IControl): IState {
      return {
        ...state,
        control: payload
      };
    },
    setLayers(state: IState, payload: Partial<ILayers>): IState {
      return {
        ...state,
        layers: {
          ...state.layers,
          ...payload
        }
      };
    },
    setFilter(state: IState, payload: Partial<IFilter>): IState {
      return { ...state, filter: { ...state.filter, ...payload } };
    },
    resetFilter(state: IState): IState {
      return { ...state, filter: initialFilter };
    },
    setHeatMap(state: IState, payload: IHeatMap): IState {
      return { ...state, heatMap: payload };
    },
    resetHeatMap(state: IState): IState {
      return { ...state, heatMap: null };
    },
    reset(): IState {
      return { ...initialState, filter: initialFilter };
    }
  },
  effects: () => ({
    async getData() {
      try {
        const [
          {
            data: { data: offices }
          },
          { data: employees }
        ] = await Promise.all([MapsService.getOffices(), UsersService.getAllEmployeesShort()]);

        this.setData({ offices, employees });
      } catch (error) {
        return error;
      }
    },
    async getHeatMap(_: any, rootState: any) {
      const { filter } = rootState.reportHeatMap;

      const cloneFilter = setStartEndOfDay(filter, ['from', 'to']);

      try {
        const [{ data: map }, planImg, { data }] = await Promise.all([
          MapsService.get(cloneFilter.mapId),
          MapsService.getMapImage(cloneFilter.mapId),
          ReportService.getJsonHeatMap(cloneFilter)
        ]);

        this.setHeatMap({ map, planImg, data });
        this.setLayers({ isGeo: true });
      } catch (error) {
        return error;
      }
    }
  })
});
